import React, { useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { Box, Tooltip } from '@mui/material'
import { BriefcaseIcon, BuildingIcon, PlusIcon, UserPlusIcon } from 'lucide-react'
import { Button, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { SALES_STAGE_LABELS, SALES_STAGES } from '@repo/types'

import { Header } from 'components'
import { useCanAccess } from 'hooks'
import { legacySalesTabTarget, SALES_TABS, type SalesTab } from 'routes'

import { OUTREACH_ADMIN_TIP, OutreachCampaignsPanel } from '../GtmOutreachPage/OutreachCampaignsPanel'
import { PlaybookPanel, TemplatesPanel } from '../GtmOutreachPage/PlaybookPanels'
import { MANAGE_SALES_TIP } from './dealsModel'
import { SalesCompaniesPanel, SalesDealsPanel, SalesPeoplePanel } from './SalesListPanels'
import { SalesPipelinePanel } from './SalesPipelinePanel'
import { useSalesWorkspace } from './useSalesWorkspace'

const TAB_LABELS: Record<SalesTab, string> = {
  pipeline: 'Pipeline',
  deals: 'Deals',
  companies: 'Companies',
  people: 'People',
  campaigns: 'Campaigns',
  playbook: 'Playbook',
  templates: 'Templates',
}
const TABS = SALES_TABS.map((value) => ({ value, label: TAB_LABELS[value] }))

const DESCRIPTIONS: Record<SalesTab, string> = {
  pipeline:
    `Every deal, from any channel: ${SALES_STAGES.map((s) => SALES_STAGE_LABELS[s]).join(', ')}. Vera moves a deal when it sees the evidence.`,
  deals: 'Every deal, open and closed. A company can have several: an upsell or a rollout is a deal of its own.',
  companies: 'The brands on the pipeline and their deals. Not platform companies: one links to its platform company once it exists.',
  people: 'The people on the pipeline. Waitlist signups arrive on their own; add anyone else by hand. Each shows their company’s stage, or Lead.',
  campaigns:
    'Email outreach and field visits: the brands each campaign targets and where each one stands. Nothing here sends email.',
  playbook: 'How a field visit is run and which brands qualify. A read-only copy; where it comes from is noted below.',
  templates: 'The outreach emails: an opening per industry, and the follow-ups. A read-only copy to send from your own inbox.',
}

/**
 * `/internal/gtm/sales` — Traction channel #13 (Sales), one GTM row with seven tabs (VRG-713 order):
 *
 *   Pipeline   VRG-691: the deals board, KPI tiles, the flow chart, counts by channel (default)
 *   Deals      VRG-701: every deal, open and closed
 *   Companies  VRG-691: the brands on the pipeline (prospects, not platform tenants)
 *   People     VRG-691: the people on the pipeline ("Leads" until VRG-727; `?tab=leads` redirects)
 *   Campaigns  VRG-679 campaigns (the tab was "Outreach" until VRG-713); one opens at
 *              /internal/gtm/sales/campaigns/:id
 *   Playbook   } read-only snapshot of the field-visits folder, with its provenance
 *   Templates  }
 *
 * THE TAB IS IN THE URL (`?tab=`), and switching keeps every other param (Pipeline's `range=`
 * and `source=`). The old `?tab=outreach` is rewritten to `?tab=campaigns`, and `?tab=leads` to
 * `?tab=people`, here, in place (`legacySalesTabTarget`), so a bookmarked or emailed link still lands
 * on its tab. The Sales forms, the company drawer and the confirm gates are owned once by
 * `useSalesWorkspace`, so "Add person" here, in the drawer and on a list row is one form (§28).
 *
 * Gate: `internal_gtm_view` (routes/internalAccess.ts). Sales writes need `internal_sales_manage`
 * ("Manage sales"); campaign writes are platform_admin. Both show disabled with the reason (§27).
 */
export const GtmSalesPage = () => {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const { search } = location
  const legacyTarget = legacySalesTabTarget(search)
  const requested = params.get('tab')
  const tab: SalesTab = (SALES_TABS as readonly string[]).includes(requested ?? '') ? (requested as SalesTab) : 'pipeline'
  const setTab = (value: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', value)
    setParams(next, { replace: true })
  }
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const { isHasAccess: canManage } = useCanAccess({ action: 'internal_sales_manage' })
  const [creating, setCreating] = useState(false)
  const workspace = useSalesWorkspace()
  const salesTab = tab === 'pipeline' || tab === 'deals' || tab === 'people' || tab === 'companies'

  // VRG-713 (Spencer, 2026-10-16) — ONE button per tab, the tab's own object: Add deal on Pipeline
  // and Deals, Add company on Companies, Add person on People. Nothing is lost: Add deal (and Add person)
  // create a company inline ("New company…") and Add deal adds people by email; a Companies row and
  // the company drawer keep Add person and Add deal. §26 one primary; §27 visible, disabled with the
  // permission named.
  const gated = (label: string, onClick: () => void, icon: React.ReactNode) => (
    <Tooltip key={label} title={canManage ? '' : MANAGE_SALES_TIP}>
      <span style={{ display: 'inline-flex' }}>
        <Button variant="primary" disableElevation disabled={!canManage} onClick={onClick} startIcon={icon}>
          {label}
        </Button>
      </span>
    </Tooltip>
  )
  const salesAction =
    tab === 'companies'
      ? gated('Add company', workspace.actions.addCompany, <BuildingIcon strokeWidth={1} />)
      : tab === 'people'
        ? gated('Add person', () => workspace.actions.addLead(null), <UserPlusIcon strokeWidth={1} />)
        : gated('Add deal', () => workspace.actions.addDeal(null), <BriefcaseIcon strokeWidth={1} />)

  if (legacyTarget) return <Navigate to={legacyTarget} replace state={location.state} />

  return (
    <>
      <Header
        title="Sales"
        description={DESCRIPTIONS[tab]}
        rightComponents={
          salesTab ? (
            salesAction
          ) : tab === 'campaigns' ? (
            <Tooltip title={isAdmin ? '' : OUTREACH_ADMIN_TIP}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  variant="primary"
                  disableElevation
                  disabled={!isAdmin}
                  onClick={() => setCreating(true)}
                  startIcon={<PlusIcon strokeWidth={1} />}
                >
                  New campaign
                </Button>
              </span>
            </Tooltip>
          ) : undefined
        }
      />
      <Tabs redirect={false} tabs={TABS} value={tab} onChange={setTab} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }}>
        {tab === 'campaigns' ? (
          <OutreachCampaignsPanel isAdmin={isAdmin} creating={creating} onCreatingChange={setCreating} />
        ) : tab === 'playbook' ? (
          <PlaybookPanel />
        ) : tab === 'templates' ? (
          <TemplatesPanel />
        ) : tab === 'deals' ? (
          <SalesDealsPanel actions={workspace.actions} canManage={canManage} />
        ) : tab === 'people' ? (
          <SalesPeoplePanel actions={workspace.actions} canManage={canManage} />
        ) : tab === 'companies' ? (
          <SalesCompaniesPanel actions={workspace.actions} canManage={canManage} canCreateDemo={workspace.canCreateDemo} />
        ) : (
          <SalesPipelinePanel actions={workspace.actions} canManage={canManage} />
        )}
      </Box>
      {workspace.modals(canManage)}
    </>
  )
}
